import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  let url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
    if (input !== undefined) {
      url = `${url}?input=${encodeURIComponent(JSON.stringify(input))}`
    }
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

// ─── Types ────────────────────────────────────────────────────────

export type DeckSource = "knowledge" | "mistake" | "vocab" | "imported" | "manual"
export type SrsRating = "again" | "hard" | "good" | "easy"
export type ProgressStatus = "new" | "learning" | "review" | "mastered"

export interface DeckListItem {
  id: string
  name: string
  description: string | null
  subject: string | null
  source: DeckSource
  cardCount: number
  dueCount: number
  createdAt: string
  updatedAt: string
}

export interface CardProgressInfo {
  status: string
  easeFactor: number
  intervalDays: number
  repetitions: number
  dueAt: string | null
  lastReviewedAt?: string | null
}

export interface CardItem {
  id: string
  deckId: string
  front: string
  back: string
  hint: string | null
  contentType: string
  source: string
  sourceRef: string | null
  tags: string[] | null
  createdAt: string
  updatedAt: string
  progress: CardProgressInfo | null
}

export interface CardListResponse {
  items: CardItem[]
  total: number
  page: number
  pageSize: number
}

export interface StudyCard {
  cardId: string
  front: string
  back: string
  hint: string | null
  contentType: string
  tags: string[] | null
  deckId: string
  deckName: string
  progress: {
    status: string
    intervalDays: number
    repetitions: number
    dueAt: string | null
  } | null
}

export interface TodayStats {
  totalDue: number
  totalDueActive: number
  decks: Array<{
    deckId: string
    deckName: string
    dueCount: number
    dueActiveCount: number
  }>
}

export interface ReviewResult {
  cardId: string
  rating: SrsRating
  status: ProgressStatus
  intervalDays: number
  easeFactor: number
  dueAt: string
}

export interface GenerateResult {
  deckId: string
  deckName: string
  created: number
  skipped: number
}

export interface FlashcardTemplate {
  id: string
  name: string
  description: string
}

// ─── Deck API ─────────────────────────────────────────────────────

export async function fetchDecks(): Promise<DeckListItem[]> {
  return tRPCRequest<DeckListItem[]>("flashcards.deck.list", undefined, "GET")
}

export async function createDeck(params: {
  name: string
  description?: string
  subject?: string
}): Promise<DeckListItem> {
  return tRPCRequest("flashcards.deck.create", params, "POST")
}

export async function updateDeck(params: {
  deckId: string
  name?: string
  description?: string | null
  subject?: string | null
}): Promise<DeckListItem> {
  return tRPCRequest("flashcards.deck.update", params, "POST")
}

export async function deleteDeck(deckId: string): Promise<{ success: boolean }> {
  return tRPCRequest("flashcards.deck.delete", { deckId }, "POST")
}

// ─── Card API ─────────────────────────────────────────────────────

export async function fetchCards(params: {
  deckId: string
  page?: number
  pageSize?: number
}): Promise<CardListResponse> {
  return tRPCRequest<CardListResponse>("flashcards.card.list", params, "GET")
}

export async function createCard(params: {
  deckId: string
  front: string
  back: string
  hint?: string
  contentType?: string
  tags?: string[]
}): Promise<CardItem> {
  return tRPCRequest("flashcards.card.create", params, "POST")
}

export async function updateCard(params: {
  cardId: string
  front?: string
  back?: string
  hint?: string | null
  tags?: string[]
}): Promise<CardItem> {
  return tRPCRequest("flashcards.card.update", params, "POST")
}

export async function deleteCard(cardId: string): Promise<{ success: boolean }> {
  return tRPCRequest("flashcards.card.delete", { cardId }, "POST")
}

// ─── Study API ────────────────────────────────────────────────────

export async function fetchTodayStats(): Promise<TodayStats> {
  return tRPCRequest<TodayStats>("flashcards.study.todayStats", undefined, "GET")
}

export async function fetchNextBatch(params: {
  deckId?: string
  limit?: number
}): Promise<StudyCard[]> {
  return tRPCRequest<StudyCard[]>("flashcards.study.nextBatch", params, "GET")
}

export async function reviewCard(params: {
  cardId: string
  rating: SrsRating
}): Promise<ReviewResult> {
  return tRPCRequest("flashcards.study.review", params, "POST")
}

// ─── Generation API ───────────────────────────────────────────────

export async function fetchFlashcardTemplates(): Promise<FlashcardTemplate[]> {
  return tRPCRequest<FlashcardTemplate[]>("flashcards.template.list", undefined, "GET")
}

export async function generateFromKnowledgePoints(params: {
  kpCodes: string[]
  deckId?: string
  deckName?: string
  template?: string
}): Promise<GenerateResult> {
  return tRPCRequest("flashcards.generate.fromKnowledgePoints", params, "POST")
}

export async function generateFromMistakes(params: {
  mistakeRecordIds: string[]
  deckId?: string
  deckName?: string
  template?: string
}): Promise<GenerateResult> {
  return tRPCRequest("flashcards.generate.fromMistakes", params, "POST")
}

export async function generateVocab(params: {
  paperId: string
  deckId?: string
  deckName?: string
  template?: string
}): Promise<GenerateResult> {
  return tRPCRequest("flashcards.generate.vocab", params, "POST")
}
